import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, LinkIcon } from '@heroicons/react/24/outline';
import { documentRelationshipTypeService } from '../../../services/documentRelationshipTypeService';
import { DocumentRelationshipType } from '../../../types/documents';
import { useToast } from '../../../components/UI/ToastProvider';

type FormState = {
  id?: string;
  name: string;
  description: string;
  isActive: boolean;
};

const initialFormState: FormState = {
  name: '',
  description: '',
  isActive: true,
};

export const DocumentRelationshipTypesPage: React.FC = () => {
  const { addToast } = useToast();
  const [types, setTypes] = useState<DocumentRelationshipType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [saving, setSaving] = useState(false);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const data = await documentRelationshipTypeService.getAll(true);
      setTypes(data);
    } catch {
      addToast('Erro ao carregar tipos de relacionamento.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTypes();
  }, []);

  const filteredTypes = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return types;
    return types.filter((item) =>
      item.name.toLowerCase().includes(term) ||
      item.description?.toLowerCase().includes(term),
    );
  }, [types, search]);

  const openCreate = () => {
    setFormState(initialFormState);
    setShowForm(true);
  };

  const openEdit = (item: DocumentRelationshipType) => {
    if (item.isSystem) return;
    setFormState({
      id: item.id,
      name: item.name,
      description: item.description || '',
      isActive: item.isActive,
    });
    setShowForm(true);
  };

  const onSave = async () => {
    if (!formState.name.trim()) {
      addToast('Informe o nome do tipo.', 'error');
      return;
    }

    setSaving(true);
    try {
      if (formState.id) {
        await documentRelationshipTypeService.update(formState.id, {
          name: formState.name.trim(),
          description: formState.description.trim(),
          isActive: formState.isActive,
        });
        addToast('Tipo atualizado com sucesso.', 'success');
      } else {
        await documentRelationshipTypeService.create({
          name: formState.name.trim(),
          description: formState.description.trim(),
        });
        addToast('Tipo criado com sucesso.', 'success');
      }

      setShowForm(false);
      await loadTypes();
    } catch (error: any) {
      addToast(error?.response?.data?.message || 'Não foi possível salvar o tipo.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item: DocumentRelationshipType) => {
    if (item.isSystem) {
      addToast('Tipos sistêmicos não podem ser removidos.', 'error');
      return;
    }

    const confirmed = window.confirm(`Deseja remover o tipo "${item.name}"?`);
    if (!confirmed) return;

    try {
      await documentRelationshipTypeService.remove(item.id);
      addToast('Tipo removido com sucesso.', 'success');
      await loadTypes();
    } catch (error: any) {
      addToast(error?.response?.data?.message || 'Não foi possível remover o tipo.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tipos de Relacionamento</h1>
          <p className="text-sm text-gray-600">
            Gerencie os tipos usados no vínculo entre documentos.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Novo tipo
        </button>
      </div>

      <div className="max-w-md">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar tipos..."
          className="w-full rounded-md border border-gray-300 px-3 py-2"
        />
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Carregando...</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredTypes.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.description || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {item.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {item.isSystem ? (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700">Sistêmico</span>
                    ) : (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">Customizado</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <div className="relative group/edit">
                        <button
                          onClick={() => openEdit(item)}
                          disabled={item.isSystem}
                          className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-40"
                          title={item.isSystem ? 'Tipo sistêmico não pode ser editado' : 'Editar'}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        {item.isSystem && (
                          <div className="pointer-events-none absolute bottom-full right-0 mb-1 hidden whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-sm group-hover/edit:block">
                            Tipos sistêmicos não podem ser editados
                          </div>
                        )}
                      </div>
                      <div className="relative group/delete">
                        <button
                          onClick={() => void onDelete(item)}
                          disabled={item.isSystem}
                          className="p-1 text-gray-500 hover:text-red-600 disabled:opacity-40"
                          title={item.isSystem ? 'Tipo sistêmico não pode ser removido' : 'Excluir'}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                        {item.isSystem && (
                          <div className="pointer-events-none absolute bottom-full right-0 mb-1 hidden whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-sm group-hover/delete:block">
                            Tipos sistêmicos não podem ser removidos
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredTypes.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    Nenhum tipo de relacionamento encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-blue-600" />
              {formState.id ? 'Editar tipo' : 'Novo tipo'}
            </h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nome</label>
                <input
                  type="text"
                  value={formState.name}
                  onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                <textarea
                  value={formState.description}
                  onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  rows={3}
                />
              </div>
              {formState.id && (
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formState.isActive}
                    onChange={(e) => setFormState((prev) => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Tipo ativo
                </label>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={() => void onSave()}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

